import { useState, useEffect } from "react";
import { User, Lock, Bell, Download, Eye, EyeOff, Loader2, Check, Smartphone, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useToast } from "@/hooks/use-toast";
import { useProfile } from "@/hooks/useProfile";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { useAuth } from "@/contexts/AuthContext";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { detectDevice, getShortcutInstructions, getBrowserName, getOSName } from "@/utils/deviceDetection";

interface ProfileSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ProfileSheet = ({ open, onOpenChange }: ProfileSheetProps) => {
  const { user } = useAuth();
  const { profile, updateProfile, updatePassword } = useProfile();
  const { 
    preferences, 
    updateShortcutPermission, 
    updatePushSubscription, 
    disablePushNotifications
  } = useUserPreferences();
  const { toast } = useToast();

  const [name, setName] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  const { promptInstall, canPrompt, isInstalled } = usePWAInstall();

  const device = detectDevice();
  const shortcutInstructions = getShortcutInstructions(device);

  // Check if running as installed PWA
  const isStandalonePWA = isInstalled || device.isStandalone;

  useEffect(() => {
    if (profile) {
      setName(profile.name);
    }
  }, [profile]);

  useEffect(() => {
    if (preferences) {
      setNotificationsEnabled(preferences.pushEnabled);
    }
  }, [preferences]);

  const handleSaveName = async () => {
    if (!name.trim()) {
      toast({
        title: "Erro",
        description: "O nome não pode estar vazio",
        variant: "destructive",
      });
      return;
    }

    setSavingName(true);
    const { error } = await updateProfile({ name: name.trim() });
    setSavingName(false);

    if (error) {
      toast({
        title: "Erro ao salvar",
        description: error,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Perfil atualizado",
        description: "Seu nome foi atualizado com sucesso",
      });
    }
  };

  const handleSavePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({
        title: "Erro",
        description: "Preencha todos os campos de senha",
        variant: "destructive",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: "Erro",
        description: "As senhas não coincidem",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: "Erro",
        description: "A nova senha deve ter pelo menos 6 caracteres",
        variant: "destructive",
      });
      return;
    }

    setSavingPassword(true);
    const { error } = await updatePassword(currentPassword, newPassword);
    setSavingPassword(false);

    if (error) {
      toast({
        title: "Erro ao alterar senha",
        description: error,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Senha alterada",
        description: "Sua senha foi alterada com sucesso",
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  const handleToggleNotifications = async (enabled: boolean) => {
    if (enabled) {
      // Request notification permission
      if ("Notification" in window) {
        const permission = await Notification.requestPermission();
        if (permission === "granted") {
          // Here you would register the push subscription
          // For now, just update the preference
          await updatePushSubscription({ enabled: true });
          setNotificationsEnabled(true);
          toast({
            title: "Notificações ativadas",
            description: "Você receberá notificações do app",
          });
        } else {
          toast({
            title: "Permissão negada",
            description: "Você precisa permitir notificações nas configurações do navegador",
            variant: "destructive",
          });
        }
      }
    } else {
      await disablePushNotifications();
      setNotificationsEnabled(false);
      toast({
        title: "Notificações desativadas",
        description: "Você não receberá mais notificações push",
      });
    }
  };

  const handleInstallPWA = async () => {
    if (canPrompt) {
      const result = await promptInstall();
      if (result.outcome === "accepted") {
        await updateShortcutPermission("created");
        toast({
          title: "App instalado!",
          description: "O HabitFrog foi adicionado à sua tela inicial",
        });
      }
    } else {
      // Show manual instructions
      toast({
        title: "Instruções de instalação",
        description: shortcutInstructions,
      });
    }
  };

  const handleCreateShortcut = async () => {
    await updateShortcutPermission("created");
    toast({
      title: "Ótimo!",
      description: "Atalho marcado como criado",
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Meu Perfil
          </SheetTitle>
          <SheetDescription>
            Gerencie suas informações e preferências
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Profile Information */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <User className="h-4 w-4" />
              Informações Pessoais
            </h3>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={user?.email || ""}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                O email não pode ser alterado
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Nome</Label>
              <div className="flex gap-2">
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Seu nome"
                />
                <Button
                  onClick={handleSaveName}
                  disabled={savingName || name === profile?.name}
                  size="sm"
                >
                  {savingName ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          <Separator />

          {/* Password Change */}
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="password">
              <AccordionTrigger className="hover:no-underline">
                <span className="flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Alterar Senha
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Senha Atual</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Digite sua senha atual"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword">Nova Senha</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Digite a nova senha"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmar Nova Senha</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirme a nova senha"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <Button
                  onClick={handleSavePassword}
                  disabled={savingPassword || !currentPassword || !newPassword || !confirmPassword}
                  className="w-full"
                >
                  {savingPassword ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Salvando...
                    </>
                  ) : (
                    "Alterar Senha"
                  )}
                </Button>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <Separator />

          {/* Preferences */}
          <div className="space-y-4">
            <h3 className="font-medium">Preferências</h3>

            {/* Notifications */}
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  <span className="font-medium">Notificações Push</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Receba lembretes sobre seus hábitos
                </p>
              </div>
              <Switch
                checked={notificationsEnabled}
                onCheckedChange={handleToggleNotifications}
              />
            </div>

            {/* App Installation / Shortcut */}
            <div className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Smartphone className="h-4 w-4" />
                <span className="font-medium">Instalar App</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {getBrowserName(device.browser)} no {getOSName(device.os)}
              </p>
              {isStandalonePWA ? (
                <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  Você já está usando o app instalado!
                </p>
              ) : (
                <div className="space-y-2">
                  {canPrompt ? (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleInstallPWA}
                      className="w-full"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Instalar App
                    </Button>
                  ) : (
                    <>
                      <p className="text-xs text-muted-foreground bg-muted p-2 rounded">
                        {shortcutInstructions}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCreateShortcut}
                        className="w-full"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Já instalei!
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
